import axiosInstance from "service/axiosInstance";
import { PaginatedActivities } from "types/crm/activity";
import {
  CreateDealPayload,
  Deal,
  DealAssociatedCompany,
  DealAssociatedContact,
  DealDetail,
  DealKanbanColumn,
  DealKanbanPosition,
  DealOption,
  DealOptionsPage,
  DealStage,
  DealUuidResolution,
  GetDealKanbanParams,
  GetDealOptionsParams,
  GetDealsParams,
  UpdateDealAssociationsPayload,
  UpdateDealKanbanPositionPayload,
  UpdateDealPayload,
} from "types/crm/deal";
import { toCrmPage } from "./pagination";
import { CrmPage } from "types/crm/pagination";
import { CreateDealNotePayload, Note, UpdateCrmDealsNotePayload } from "types/crm/notes";
import {
  CreateCrmDealTaskPayload,
  CrmTask,
  PaginatedTasks,
  TaskResponse,
  TasksListResponse,
  TaskStatus,
  TaskQuickFilter,
} from "types/crm/task";
import { CrmComment, CrmCommentRepliesPage } from "types/crm/comments";

/**
 * Payload for updating a deal task. All fields besides the identifiers are
 * optional so callers can send a partial patch (e.g. just `status`).
 * NOTE: ideally this lives in types/crm/task.ts alongside CreateCrmDealTaskPayload;
 * declared here for now so nothing else has to change shape.
 */
export interface UpdateCrmDealTaskPayload {
  dealUuid: string;
  taskUuid: string;
  title?: string;
  description?: string;
  dueAt?: string;
  taskOwnerUuid?: string | null;
  taskType?: string;
  priority?: string;
  timezone?: string;
  status?: TaskStatus;
}

export const dealQueryKeys = {
  all: ["deals"] as const,
  lists: () => [...dealQueryKeys.all, "list"] as const,
  list: (params: GetDealsParams = {}) =>
    [...dealQueryKeys.lists(), params] as const,
  // Under `all`, so every deal write that invalidates `all` refreshes the board too
  kanban: (params: GetDealKanbanParams) =>
    [...dealQueryKeys.all, "kanban", params] as const,
  resolve: (dealKey: string) =>
    [...dealQueryKeys.all, "resolve", dealKey] as const,
  detail: (dealUuid: string) =>
    [...dealQueryKeys.all, "detail", dealUuid] as const,
  contacts: (dealUuid: string) =>
    [...dealQueryKeys.detail(dealUuid), "contacts"] as const,
  companies: (dealUuid: string) =>
    [...dealQueryKeys.detail(dealUuid), "companies"] as const,
  byContact: (contactUuid: string) =>
    [...dealQueryKeys.all, "contact", contactUuid] as const,
  byCompany: (companyUuid: string) =>
    [...dealQueryKeys.all, "company", companyUuid] as const,
  // Nested under byContact so link/unlink invalidations refresh this too
  options: (contactUuid: string, params: GetDealOptionsParams = {}) =>
    [...dealQueryKeys.byContact(contactUuid), "options", params] as const,
  // Same idea, nested under byCompany
  companyOptions: (companyUuid: string, params: GetDealOptionsParams = {}) =>
    [...dealQueryKeys.byCompany(companyUuid), "options", params] as const,
  notes: (dealUuid: string) =>
    [...dealQueryKeys.all, "notes", dealUuid] as const,
  tasks: (dealUuid: string, status?: TaskStatus, quickFilter?: TaskQuickFilter) =>
    [...dealQueryKeys.all, "tasks", dealUuid, status ?? "ALL", quickFilter ?? "NONE"] as const,
  activities: (dealUuid: string) =>
    [...dealQueryKeys.all, "activities", dealUuid] as const,
  noteComments: (dealUuid: string, dealNoteUuid: string) =>
    [...dealQueryKeys.all, "note-comments", dealUuid, dealNoteUuid] as const,
  noteCommentReplies: (dealUuid: string, dealNoteUuid: string, commentUuid: string, page: number) =>
    [...dealQueryKeys.noteComments(dealUuid, dealNoteUuid), "replies", commentUuid, page] as const,
  taskComments: (dealUuid: string, dealNoteUuid: string) =>
    [...dealQueryKeys.all, "task-comments", dealUuid, dealNoteUuid] as const,
  taskCommentReplies: (dealUuid: string, dealNoteUuid: string, commentUuid: string, page: number) =>
    [...dealQueryKeys.taskComments(dealUuid, dealNoteUuid), "replies", commentUuid, page] as const,
};

export const dealApi = {
  getDeals: async ({
    search,
    dealStage,
    page = 0,
    size = 20,
  }: GetDealsParams = {}): Promise<CrmPage<Deal, DealStage>> => {
    const { data } = await axiosInstance.get(`/crm/deals`, {
      params: {
        search: search?.trim() || undefined,
        dealStage: dealStage || undefined,
        page,
        size,
      },
    });
    return toCrmPage<Deal, DealStage>(data.data, page, size);
  },

  /**
   * GET /crm/deals/kanban — every lane, in board order, each with page 0 of its
   * cards. Lanes beyond page 0 load through `getDeals` with `dealStage`.
   */
  getDealKanban: async ({
    size = 20,
  }: GetDealKanbanParams = {}): Promise<DealKanbanColumn[]> => {
    const { data } = await axiosInstance.get(`/crm/deals/kanban`, {
      params: { size },
    });
    const lanes: Partial<DealKanbanColumn>[] = Array.isArray(data?.data) ? data.data : [];

    // The CRM API drops empty and null fields, so an empty lane can arrive
    // without `deals` and a fresh stage without its totals. Fill them in here
    // so the board never has to guard each one.
    return lanes
      .filter((lane): lane is Partial<DealKanbanColumn> & { stageKey: string } => !!lane?.stageKey)
      .map((lane) => {
        const deals = lane.deals ?? [];
        const dealCount = lane.dealCount ?? deals.length;
        return {
          ...lane,
          stageUuid: lane.stageUuid ?? lane.stageKey,
          stageName: lane.stageName ?? lane.stageKey,
          status: lane.status ?? "ACTIVE",
          stageOrder: lane.stageOrder ?? 0,
          closedWon: !!lane.closedWon,
          closedLost: !!lane.closedLost,
          dealCount,
          totalAmount: lane.totalAmount ?? 0,
          hasMore: lane.hasMore ?? dealCount > deals.length,
          deals,
        };
      });
  },

  /** Step 1: dealKey (e.g. "D002") -> uuid. The URL carries the key, the API uses the uuid. */
  resolveDealUuidByKey: async (dealKey: string): Promise<string> => {
    const { data } = await axiosInstance.get<{ data: DealUuidResolution }>(
      `/crm/deals/key/${dealKey}/uuid`,
    );
    return data.data.uuid;
  },

  // Step 2: uuid -> full detail
  getDeal: async (dealUuid: string): Promise<DealDetail> => {
    const { data } = await axiosInstance.get(`/crm/deals/${dealUuid}`);
    return data.data;
  },

  getDealContacts: async (
    dealUuid: string,
  ): Promise<DealAssociatedContact[]> => {
    const { data } = await axiosInstance.get(`/crm/deals/${dealUuid}/contacts`);
    return data.data?.content ?? data.data ?? [];
  },

  getDealCompanies: async (
    dealUuid: string,
  ): Promise<DealAssociatedCompany[]> => {
    const { data } = await axiosInstance.get(`/crm/deals/${dealUuid}/companies`);
    return data.data?.content ?? data.data ?? [];
  },

  getDealsByContact: async (contactUuid: string): Promise<Deal[]> => {
    const { data } = await axiosInstance.get(
      `/crm/contacts/${contactUuid}/deals`,
    );
    return data.data;
  },


  /** Deals associated with a company — the path takes a reference, we always send the uuid. */
  getDealsByCompany: async (companyUuid: string): Promise<DealDetail[]> => {
    const { data } = await axiosInstance.get(
      `/crm/companies/${companyUuid}/deals`,
    );
    return data.data?.content ?? data.data ?? [];
  },

  createDealForCompany: async ({
    companyUuid,
    ...payload
  }: CreateDealPayload & { companyUuid: string }): Promise<Deal> => {
    const { data } = await axiosInstance.post(
      `/crm/companies/${companyUuid}/deals`,
      payload,
    );
    return data.data;
  },

  /** Org deals with an `associated` flag for the company — powers the "Add existing" tab. */
  getCompanyDealOptions: async (
    companyUuid: string,
    { search, page = 0, limit = 20 }: GetDealOptionsParams = {},
  ): Promise<DealOptionsPage> => {
    const { data } = await axiosInstance.get(
      `/crm/companies/${companyUuid}/deal-options`,
      { params: { search: search?.trim() || undefined, page, limit } },
    );
    return toCrmPage<DealOption>(data.data, page, limit);
  },

  associateDealWithCompany: async ({
    companyUuid,
    dealUuid,
  }: {
    companyUuid: string;
    dealUuid: string;
  }): Promise<DealDetail> => {
    const { data } = await axiosInstance.post(
      `/crm/companies/${companyUuid}/deals/${dealUuid}`,
    );
    return data.data;
  },

  disassociateDealFromCompany: async ({
    companyUuid,
    dealUuid,
  }: {
    companyUuid: string;
    dealUuid: string;
  }): Promise<void> => {
    await axiosInstance.delete(
      `/crm/companies/${companyUuid}/deals/${dealUuid}`,
    );
  },

  getDealOptions: async (
    contactUuid: string,
    { search, page = 0, limit = 20 }: GetDealOptionsParams = {},
  ): Promise<DealOptionsPage> => {
    const { data } = await axiosInstance.get(
      `/crm/contacts/${contactUuid}/deal-options`,
      { params: { search: search?.trim() || undefined, page, limit } },
    );
    return toCrmPage<DealOption>(data.data, page, limit);
  },

  linkDealToContact: async ({
    contactUuid,
    dealUuid,
  }: {
    contactUuid: string;
    dealUuid: string;
  }): Promise<Deal> => {
    const { data } = await axiosInstance.post(
      `/crm/contacts/${contactUuid}/deals/${dealUuid}`,
    );
    return data.data;
  },

  unlinkDealFromContact: async ({
    contactUuid,
    dealUuid,
  }: {
    contactUuid: string;
    dealUuid: string;
  }): Promise<void> => {
    await axiosInstance.delete(
      `/crm/contacts/${contactUuid}/deals/${dealUuid}`,
    );
  },
  
  createStandaloneDeal: async (payload: CreateDealPayload): Promise<Deal> => {
    const { data } = await axiosInstance.post(`/crm/deals`, payload);
    return data.data;
  },

  createDeal: async ({
    contactUuid,
    ...payload
  }: CreateDealPayload & { contactUuid: string }): Promise<Deal> => {
    const { data } = await axiosInstance.post(
      `/crm/contacts/${contactUuid}/deals`,
      payload,
    );
    return data.data;
  },

  updateDeal: async ({
    dealUuid,
    ...payload
  }: UpdateDealPayload): Promise<DealDetail> => {
    const { data } = await axiosInstance.patch(
      `/crm/deals/${dealUuid}`,
      payload,
    );
    return data.data;
  },

  updateDealKanbanPosition: async ({
    dealUuid,
    timezone = Intl.DateTimeFormat().resolvedOptions().timeZone,
    ...payload
  }: UpdateDealKanbanPositionPayload): Promise<DealKanbanPosition> => {
    const { data } = await axiosInstance.patch(
      `/crm/deals/${dealUuid}/kanban-position`,
      { ...payload, timezone },
    );
    return data.data;
  },

  updateDealAssociations: async ({
    dealUuid,
    ...payload
  }: UpdateDealAssociationsPayload): Promise<DealDetail> => {
    const { data } = await axiosInstance.patch(
      `/crm/deals/${dealUuid}/associations`,
      payload,
    );
    return data.data;
  },

  deleteDeal: async ({ dealUuid }: { dealUuid: string }): Promise<void> => {
    await axiosInstance.delete(`/crm/deals/${dealUuid}`);
  },

  // ---------------------------------------------------------------------------
  // Task APIs
  // ---------------------------------------------------------------------------

  getDealTasks: async (
    dealUuid: string,
    page = 0,
    size = 20,
    status?: TaskStatus,
    quickFilter?: TaskQuickFilter,
  ): Promise<PaginatedTasks> => {
    const { data } = await axiosInstance.get<TasksListResponse>(
      `/crm/deals/${dealUuid}/tasks`,
      { params: { page, size, status: status || undefined, quickFilter } },
    );
    return data.data;
  },

  createDealTask: async (
    payload: CreateCrmDealTaskPayload,
  ): Promise<CrmTask> => {
    const { dealUuid, ...rest } = payload;
    const { data } = await axiosInstance.post<TaskResponse>(
      `/crm/deals/${dealUuid}/tasks`,
      rest,
    );
    return data.data;
  },

  updateDealTask: async (
    payload: UpdateCrmDealTaskPayload,
  ): Promise<CrmTask> => {
    const { dealUuid, taskUuid, ...rest } = payload;
    const { data } = await axiosInstance.patch<TaskResponse>(
      `/crm/deals/${dealUuid}/tasks/${taskUuid}`,
      rest,
    );
    return data.data;
  },

  deleteDealTask: async (dealUuid: string, taskUuid: string): Promise<void> => {
    await axiosInstance.delete(`/crm/deals/${dealUuid}/tasks/${taskUuid}`);
  },

 // ---------------------------------------------------------------------------
  // Notes APIs
  // ---------------------------------------------------------------------------
  
  getDealNotes: async (dealUuid: string, page = 0, size = 20) => {
    const { data } = await axiosInstance.get(`/crm/deals/${dealUuid}/notes`, {
      params: { page, size },
    });
    return data.data; // expects { content, page, last, totalElements, ... }
  },

  createDealNote: async ({
    dealUuid,
    ...payload
  }: CreateDealNotePayload): Promise<Note> => {
    const { data } = await axiosInstance.post(
      `/crm/deals/${dealUuid}/notes`,
      payload,
    );
    return data.data;
  },

    updateDealNote: async (payload: UpdateCrmDealsNotePayload): Promise<CrmTask> => {
    const { dealUuid, noteUuid, ...rest } = payload;
    const { data } = await axiosInstance.put<TaskResponse>(
      `/crm/deals/${dealUuid}/notes/${noteUuid}`,
      rest,
    );
    return data.data;
  },


  deleteDealNote: async (dealUuid: string, noteUuid: string): Promise<void> => {
    await axiosInstance.delete(`/crm/deals/${dealUuid}/notes/${noteUuid}`);
  },

  getDealActivities: async (
    dealUuid: string,
    page = 0,
    size = 20,
    tab = "ALL_ACTIVITIES",
  ): Promise<PaginatedActivities> => {
    const { data } = await axiosInstance.get<{ data: PaginatedActivities }>(
      `/crm/deals/${dealUuid}/activities`,
      { params: { tab, page, size } },
    );
    return data.data;
  },

  // ---------------------------------------------------------------------------
  // Comment APIs
  // ---------------------------------------------------------------------------
  
  addDealNoteComment: async (
    dealUuid: string,
    dealNoteUuid: string,
    commentText: string,
    parentCommentUuid?: string | null,
  ): Promise<CrmComment> => {
    const { data } = await axiosInstance.post(
      `/crm/deals/${dealUuid}/notes/${dealNoteUuid}/comments`,
      { commentText, parentCommentUuid: parentCommentUuid ?? null },
    );
    return data.data;
  },

  updateDealNoteComment: async (
    dealUuid: string,
    dealNoteUuid: string,
    commentUuid: string,
    commentText: string,
    parentCommentUuid?: string | null,
  ): Promise<CrmComment> => {
    const { data } = await axiosInstance.put(
      `/crm/deals/${dealUuid}/notes/${dealNoteUuid}/comments/${commentUuid}`,
      { commentText, parentCommentUuid: parentCommentUuid ?? null },
    );
    return data.data;
  },

  deleteDealNoteComment: async (
    dealUuid: string,
    dealNoteUuid: string,
    commentUuid: string,
  ): Promise<void> => {
    await axiosInstance.delete(
      `/crm/deals/${dealUuid}/notes/${dealNoteUuid}/comments/${commentUuid}`,
    );
  },

  getDealNoteCommentReplies: async (
    dealUuid: string,
    dealNoteUuid: string,
    commentUuid: string,
    page = 0,
    size = 20,
  ): Promise<CrmCommentRepliesPage> => {
    const { data } = await axiosInstance.get(
      `/crm/deals/${dealUuid}/notes/${dealNoteUuid}/comments/${commentUuid}/replies`,
      { params: { page, size } },
    );
    return data.data;
  },

  addDealTaskComment: async (
    dealUuid: string,
    dealTaskUuid: string,
    commentText: string,
    parentCommentUuid?: string | null,
  ): Promise<CrmComment> => {
    const { data } = await axiosInstance.post(
      `/crm/deals/${dealUuid}/tasks/${dealTaskUuid}/comments`,
      { commentText, parentCommentUuid: parentCommentUuid ?? null },
    );
    return data.data;
  },

  updateDealTaskComment: async (
    dealUuid: string,
    dealTaskUuid: string,
    commentUuid: string,
    commentText: string,
    parentCommentUuid?: string | null,
  ): Promise<CrmComment> => {
    const { data } = await axiosInstance.put(
      `/crm/deals/${dealUuid}/tasks/${dealTaskUuid}/comments/${commentUuid}`,
      { commentText, parentCommentUuid: parentCommentUuid ?? null },
    );
    return data.data;
  },

  deleteDealTaskComment: async (
    dealUuid: string,
    dealTaskUuid: string,
    commentUuid: string,
  ): Promise<void> => {
    await axiosInstance.delete(
      `/crm/deals/${dealUuid}/tasks/${dealTaskUuid}/comments/${commentUuid}`,
    );
  },

  getDealTaskCommentReplies: async (
    dealUuid: string,
    dealTaskUuid: string,
    commentUuid: string,
    page = 0,
    size = 20,
  ): Promise<CrmCommentRepliesPage> => {
    const { data } = await axiosInstance.get(
      `/crm/deals/${dealUuid}/tasks/${dealTaskUuid}/comments/${commentUuid}/replies`,
      { params: { page, size } },
    );
    return data.data;
  },
};